import React from "react";
import { motion } from "framer-motion";
import { Wrench, Zap, Clock, Phone, ArrowRight } from "lucide-react";
import { CONTACT } from "@/lib/contact";

const FEATURES = [
  { icon: Wrench, title: "Quick repairs", desc: "Cracks, leaks, broken fittings, patch-and-paint." },
  { icon: Zap, title: "Urgent works", desc: "Same-week emergency response for property issues." },
  { icon: Clock, title: "Maintenance", desc: "One-off or ongoing property upkeep for landlords." },
  { icon: Phone, title: "Fast response", desc: "Real people answering — usually same-day." },
];

export default function SmallJobs() {
  return (
    <section
      id="small-jobs"
      data-testid="small-jobs-section"
      className="bg-white text-[#2B2420] py-20 md:py-28"
    >
      <div className="px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#B8732C] mb-5">
            <span className="brand-gold-divider" /> Small Jobs Welcome
          </div>
          <h2
            className="font-black tracking-tighter leading-[0.95] text-4xl md:text-5xl lg:text-6xl"
            style={{ fontFamily: "Cabinet Grotesk" }}
          >
            No job too small.
          </h2>
          <p className="mt-5 text-[#4D4037] text-base md:text-lg leading-relaxed max-w-md">
            A dripping tap, a door rehang, a quick skim. We show up, fix it
            properly, and leave the place clean.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="#quote"
              data-testid="small-jobs-cta"
              className="inline-flex items-center gap-2 bg-[#B8732C] hover:bg-[#9A5D1E] text-white px-6 py-4 text-sm font-bold uppercase tracking-[0.15em] transition-colors"
            >
              Request a Free Quote <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={`tel:${CONTACT.primary.tel}`}
              data-testid="small-jobs-call"
              className="inline-flex items-center gap-2 border border-[#2B2420] hover:bg-[#2B2420] hover:text-white text-[#2B2420] px-6 py-4 text-sm font-bold uppercase tracking-[0.15em] transition-colors"
            >
              <Phone className="w-4 h-4" /> Call Now
            </a>
          </div>
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 border-t border-l border-[#E5DDD0]">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                data-testid={`small-jobs-feature-${i}`}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="border-r border-b border-[#E5DDD0] p-6 md:p-7 bg-white hover:bg-[#F5EFE5] transition-colors"
              >
                <Icon className="w-6 h-6 text-[#C9A24C]" strokeWidth={1.5} />
                <h3
                  className="mt-6 font-black tracking-tight text-xl leading-tight"
                  style={{ fontFamily: "Cabinet Grotesk" }}
                >
                  {f.title}
                </h3>
                <p className="mt-2 text-sm text-[#8A7F74] leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
